import { Tooltip } from '@mui/material';
import { uniqBy } from 'lodash';
import React, { Dispatch, Fragment, JSX, SetStateAction, useMemo } from 'react';
import { SECURITY_ACTIONS, SecuredIconButton } from '../auth/Security';
import type { ActionClickEvent, SecuredActionType } from '../components/ActionType';
import AddActionsFab from '../components/AddActionsFab';
import ButtonWithIconGroup from '../components/ButtonWithIconGroup';
import { ACTIONS_CONSTANTS, CONSTANTS_ID } from '../constants/constants';
import { useIsWithinBreakpoints } from '../hooks/useIsWithinBreakpoints';
import I18n, { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { useStyling } from '../Theme';
import useHistory from '../utils/useHistory';
import ListViewExportMenu from './ListViewExportMenu';
import type { QuickAddButtonProps } from './types';

export const ListViewAddActions = ({
    displayAddActions = false,
    addActions = [],
    createUri = false,
    EntityEditor = undefined,
    enableImport = false,
    setImportPanelOpen,
    quickAddButtonProps = undefined,
    uri = undefined,
    setActiveEntity,
    setEditorOpen,
    onCreateStarted = undefined,
    mapEntityToCsvRows,
    tableData,
    exportFileName
}: {
    displayAddActions: boolean;
    addActions: SecuredActionType[];
    createUri: boolean;
    EntityEditor: (props?: any) => JSX.Element | undefined;
    enableImport: boolean;
    setImportPanelOpen: Dispatch<SetStateAction<boolean>>;
    quickAddButtonProps?: QuickAddButtonProps;
    uri: string;
    setActiveEntity: Dispatch<SetStateAction<string>>;
    setEditorOpen: Dispatch<SetStateAction<boolean>>;
    onCreateStarted?: () => void;
    mapEntityToCsvRows?: (data: Record<string, unknown>[]) => Record<string, unknown>[];
    tableData?: [];
    exportFileName?: string;
}) => {
    const { isMobile, isTablet } = useStyling();
    const { changeRoute } = useHistory();
    const isMobileOrTablet = isMobile || isTablet;
    const isLgOrSmaller = useIsWithinBreakpoints(['xs', 's', 'm', 'l']);
    const importTooltip = useI18n('common.import.tooltip');

    /**
     * A feature add action opens a create surface the list knows nothing about — a modal of its
     * own, most often — so the session has to start before the feature's own handler runs.
     */
    const withCreateStarted = (action: SecuredActionType): SecuredActionType => {
        if (!action.onClick) {
            return action;
        }

        return {
            ...action,
            onClick: (event?: ActionClickEvent) => {
                onCreateStarted?.();
                action.onClick(event);
            }
        };
    };

    let allActions: SecuredActionType[] = addActions.map(withCreateStarted);
    const inlineActions: SecuredActionType[] = !isMobileOrTablet
        ? allActions.filter((action) => Boolean(action?.component))
        : [];
    if (!isMobileOrTablet) {
        allActions = allActions.filter((action) => !action?.component);
    }

    const addOnClick = useMemo(() => {
        if (!createUri && !EntityEditor) {
            return undefined;
        }

        const addOnClickHandler = () => {
            onCreateStarted?.();
            if (createUri) {
                changeRoute(`${uri}/${CONSTANTS_ID.createId}`);
            } else if (EntityEditor) {
                setActiveEntity(CONSTANTS_ID.createId);
                setEditorOpen(true);
            }
        };

        return addOnClickHandler;
    }, [onCreateStarted, createUri, changeRoute, uri, EntityEditor, setActiveEntity, setEditorOpen]);

    const addAction: SecuredActionType = {
        label: <I18n token={'common.add'} />,
        icon: Icons.AddOutlined,
        securityAction: SECURITY_ACTIONS.EDIT,
        onClick: addOnClick
    };

    const uploadAction: SecuredActionType = {
        label: <I18n token={'common.upload'} />,
        icon: Icons.FileUpload,
        securityAction: SECURITY_ACTIONS.EDIT,
        onClick: () => setImportPanelOpen(true)
    };

    if (displayAddActions && isLgOrSmaller) {
        const shouldShowAdd = !allActions.length && addOnClick;
        const shouldShowUpload = isMobileOrTablet && enableImport;

        const enableQuickAddButtons = quickAddButtonProps?.enableQuickAddButtons;
        const showButtonNames = quickAddButtonProps?.showButtonNames || [];

        const quickAddActions = enableQuickAddButtons
            ? showButtonNames.map((name, index) =>
                  withCreateStarted({
                      label: name,
                      icon: quickAddButtonProps.setIcon(index),
                      securityAction: SECURITY_ACTIONS.EDIT,
                      onClick: () => quickAddButtonProps.onClickQuickAdd(index)
                  })
              )
            : [];

        allActions = uniqBy(
            [
                ...allActions,
                ...(shouldShowAdd ? [addAction] : []),
                ...(shouldShowUpload ? [uploadAction] : []),
                ...quickAddActions
            ],
            ACTIONS_CONSTANTS.label
        );
    }

    return (
        <>
            {displayAddActions && (
                <AddActionsFab
                    securedActions={allActions}
                    defaultAddAction={isMobileOrTablet ? undefined : addOnClick}
                />
            )}
            {!isMobileOrTablet &&
                inlineActions.map((action, index) => {
                    if (!action.component || action.hidden) {
                        return null;
                    }

                    if (typeof action.component === 'function') {
                        const Component = action.component;
                        return <Component key={`inline-action-${index}`} />;
                    }

                    return <Fragment key={`inline-action-${index}`}>{action.component}</Fragment>;
                })}
            {mapEntityToCsvRows && exportFileName && (
                <ListViewExportMenu
                    tableData={tableData}
                    mapEntityToCsvRows={mapEntityToCsvRows}
                    exportFileName={exportFileName}
                />
            )}
            {!isMobileOrTablet && (
                <>
                    {enableImport && (
                        <Tooltip title={importTooltip}>
                            <SecuredIconButton
                                securityAction={SECURITY_ACTIONS.EDIT}
                                onClick={() => setImportPanelOpen(true)}
                                color="primary"
                                aria-label={importTooltip}
                            >
                                <Icons.FileUpload />
                            </SecuredIconButton>
                        </Tooltip>
                    )}
                    {quickAddButtonProps?.enableQuickAddButtons && !isLgOrSmaller && (
                        <ButtonWithIconGroup
                            labels={quickAddButtonProps.showButtonNames}
                            onClick={(index) => {
                                onCreateStarted?.();
                                quickAddButtonProps.onClickQuickAdd(index);
                            }}
                            setIcon={quickAddButtonProps.setIcon}
                            sx={quickAddButtonProps.sx}
                        />
                    )}
                </>
            )}
        </>
    );
};
